/**
 * WordCloud — частотное облако слов word-вопроса (Фаза 10, И8, §D3.6).
 *
 * Чистый DOM/flex-wrap без библиотек (прецедент A11yPanel/FLIP-LiveLeaderboard):
 * слова «текут» строками по центру, размер шрифта ∝ частоте (rem — уважает
 * `--font-scale` Фазы 9 П2), цвет — стабильный хеш слова в палитре из
 * CSS-переменных `--wc-c1..c5` (не меняется при пересортировке живого облака).
 * Слова — ГОЛЫЙ текст без FormulaText/KaTeX: React экранирует, инъекция
 * формул/разметки через пользовательское слово исключена (RD6).
 *
 * Зум — три нативных уровня:
 *  1. клик/тап/Enter по слову-кнопке — подсветка + счётчик (aria «слово …,
 *     упомянуто N раз»; повторный клик/Esc — снять);
 *  2. масштаб всего облака кнопками −/100%/+ (0.5×–3×, шаг 1.25×) через
 *     CSS-переменную `--wc-zoom`; при >1× — панорамирование скроллом контейнера
 *     (мышь/тач/клавиатура-стрелки);
 *  3. полноэкранный режим `requestFullscreen()` с fixed-fallback для iOS
 *     (`word-cloud--fs`), выход — Esc/кнопка.
 *
 * Каскадная анимация появления гасится обоими слоями Фазы 9 П2:
 * `prefers-reduced-motion` (CSS) и `data-a11y-motion='reduce'` (класс не
 * ставится вообще — проверяемо в DOM).
 */
import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import type { WordCountEntry } from '../protocol';

/** Диапазон размера шрифта (rem): минимальная и максимальная частота. */
const MIN_FONT_REM = 0.9;
const MAX_FONT_REM = 2.6;

/** Границы и шаг масштаба облака (уровень зума 2). */
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 3;
const ZOOM_STEP = 1.25;

/** Задержка каскада появления: i × 30 мс. */
const APPEAR_DELAY_MS = 30;

/** Включена ли in-app настройка «уменьшить анимацию» (data-a11y-motion). */
function motionReduced(): boolean {
  if (typeof document === 'undefined') return false;
  return document.documentElement.getAttribute('data-a11y-motion') === 'reduce';
}

/** Стабильный индекс палитры слова: сумма кодовых единиц % 5. */
function colorIndex(word: string): number {
  let sum = 0;
  for (let i = 0; i < word.length; i++) sum += word.charCodeAt(i);
  return sum % 5;
}

/**
 * Размер шрифта слова (rem): линейная интерполяция count между min..max в
 * MIN_FONT_REM..MAX_FONT_REM. При равных частотах (одно слово / все поровну) —
 * средний размер: деления на ноль нет (RD1/RD2).
 */
export function wordFontSize(count: number, minCount: number, maxCount: number): number {
  if (maxCount <= minCount) return (MIN_FONT_REM + MAX_FONT_REM) / 2;
  const t = (count - minCount) / (maxCount - minCount);
  return MIN_FONT_REM + t * (MAX_FONT_REM - MIN_FONT_REM);
}

export interface WordCloudProps {
  /** Частоты (сервер сортирует count DESC → word ASC); компонент не сортирует. */
  words: WordCountEntry[];
}

export function WordCloud({ words }: WordCloudProps): JSX.Element | null {
  const { t } = useTranslation();
  const [active, setActive] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [fsFallback, setFsFallback] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const reduced = motionReduced();

  // Выход из нативного fullscreen (Esc браузера) гасит и fallback-флаг.
  useEffect(() => {
    function onChange(): void {
      if (!document.fullscreenElement) setFsFallback(false);
    }
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  if (words.length === 0) {
    // Граничный случай «0 слов» (RD1): не рендерим пустой контейнер.
    return <p className="word-cloud__empty muted">{t('word.empty')}</p>;
  }

  let minCount = words[0].count;
  let maxCount = words[0].count;
  for (const w of words) {
    if (w.count < minCount) minCount = w.count;
    if (w.count > maxCount) maxCount = w.count;
  }

  function zoomIn(): void {
    setZoom((z) => Math.min(ZOOM_MAX, z * ZOOM_STEP));
  }
  function zoomOut(): void {
    setZoom((z) => Math.max(ZOOM_MIN, z / ZOOM_STEP));
  }
  function zoomReset(): void {
    setZoom(1);
  }

  function toggleFullscreen(): void {
    const el = rootRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      void document.exitFullscreen();
      setFsFallback(false);
      return;
    }
    if (typeof el.requestFullscreen === 'function') {
      el.requestFullscreen()
        .then(() => setFsFallback(false))
        .catch(() => setFsFallback(true)); // отказ API (iframe и т.п.) — fallback.
      return;
    }
    setFsFallback((v) => !v); // iOS Safari: fixed-оверлей.
  }

  // Уровень 1: Esc снимает подсветку; стрелки панорамируют при зуме.
  function handleViewportKey(e: React.KeyboardEvent<HTMLDivElement>): void {
    if (e.key === 'Escape') {
      setActive(null);
      return;
    }
    const vp = viewportRef.current;
    if (!vp) return;
    const delta = 60;
    switch (e.key) {
      case 'ArrowLeft':
        vp.scrollBy({ left: -delta });
        e.preventDefault();
        break;
      case 'ArrowRight':
        vp.scrollBy({ left: delta });
        e.preventDefault();
        break;
      case 'ArrowUp':
        vp.scrollBy({ top: -delta });
        e.preventDefault();
        break;
      case 'ArrowDown':
        vp.scrollBy({ top: delta });
        e.preventDefault();
        break;
      default:
        break;
    }
  }

  const rootClass = [
    'word-cloud',
    fsFallback && !document.fullscreenElement ? 'word-cloud--fs' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rootClass} ref={rootRef} data-testid="word-cloud">
      <div className="word-cloud__toolbar" role="group" aria-label={t('word.cloudAria')}>
        <div className="word-cloud__zoom" role="group" aria-label={t('word.zoomGroupAria')}>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={zoomOut}
            disabled={zoom <= ZOOM_MIN}
            aria-label={t('word.zoomOut')}
            data-testid="word-zoom-out"
          >
            −
          </button>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={zoomReset}
            aria-label={t('word.zoomReset')}
            data-testid="word-zoom-reset"
          >
            {Math.round(zoom * 100)}%
          </button>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={zoomIn}
            disabled={zoom >= ZOOM_MAX}
            aria-label={t('word.zoomIn')}
            data-testid="word-zoom-in"
          >
            +
          </button>
        </div>
        <button
          type="button"
          className="btn btn--ghost"
          onClick={toggleFullscreen}
          aria-label={fsFallback ? t('word.fullscreenExit') : t('word.fullscreen')}
          data-testid="word-fullscreen"
        >
          {fsFallback ? '⤡' : '⤢'}
        </button>
      </div>

      <div
        className="word-cloud__viewport"
        ref={viewportRef}
        tabIndex={0}
        role="group"
        aria-label={t('word.cloudAria')}
        onKeyDown={handleViewportKey}
        style={{ '--wc-zoom': zoom } as CSSProperties}
        data-testid="word-cloud-viewport"
      >
        <div className="word-cloud__inner">
          {words.map((w, i) => {
            const classes = [
              'word-cloud__word',
              `word-cloud__word--c${colorIndex(w.word) + 1}`,
              active === w.word ? 'word-cloud__word--active' : '',
              reduced ? '' : 'word-cloud__word--animate',
            ]
              .filter(Boolean)
              .join(' ');
            return (
              <button
                key={w.word}
                type="button"
                className={classes}
                style={{
                  fontSize: `${wordFontSize(w.count, minCount, maxCount).toFixed(2)}rem`,
                  animationDelay: reduced ? undefined : `${i * APPEAR_DELAY_MS}ms`,
                }}
                aria-pressed={active === w.word}
                // n (не count): {{count}} в i18next включает plural-логику —
                // проект плюрализацию не использует (1:1 каталоги).
                aria-label={t('word.wordAria', { word: w.word, n: w.count })}
                onClick={() => setActive((cur) => (cur === w.word ? null : w.word))}
                data-testid={`word-cloud-word-${i}`}
              >
                {w.word}
                <span className="word-cloud__count" aria-hidden="true">
                  ×{w.count}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
